import React from "react";
import { Box, Typography } from "@mui/material";
import { COLORS } from "../../styles/uiConstants";
import { useClinicTheme } from "../../theme";

/**
 * Master-detail listesinde seçilebilir satır. Seçili satır: solda 3px tema
 * rengi şerit + slate50 zemin (PatientWorkspaceTabs sekmelerindeki satır
 * deseni). Klavyeyle erişilebilir: `role="button"`, Tab ile odak, Enter/Space
 * ile seçim.
 *
 * Yerleşim: [icon | avatar] [meta (sabit sütun)] [title + subtitle] [trailing]
 *
 * @param {boolean} [active=false] - Seçili mi
 * @param {() => void} onClick
 * @param {React.ReactNode} [icon] - Sol ikon
 * @param {React.ReactNode} [avatar] - Sol avatar (icon yerine)
 * @param {React.ReactNode} title - Ana metin (tek satır, taşarsa …)
 * @param {React.ReactNode} [subtitle] - Başlık altı ikincil metin
 * @param {React.ReactNode} [meta] - Başlığın solunda sabit genişlikli sütun (tarih vb.)
 * @param {React.ReactNode} [trailing] - Sağ taraf (durum chip'i, aksiyon)
 * @param {boolean} [disabled=false]
 * @param {object} [sx]
 */
export default function SelectableListRow({
  active = false,
  onClick,
  icon,
  avatar,
  title,
  subtitle,
  meta,
  trailing,
  disabled = false,
  sx = {},
  ...rest // aria-label vb. kök öğeye iletilir
}) {
  const { palette } = useClinicTheme();
  const tone = palette?.primary || COLORS.slate700;

  const activate = () => {
    if (disabled) return;
    onClick?.();
  };

  return (
    <Box
      {...rest}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-pressed={active}
      aria-disabled={disabled || undefined}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
      sx={{
        px: 2,
        py: 1.25,
        cursor: disabled ? "default" : "pointer",
        borderLeft: `3px solid ${active ? tone : "transparent"}`,
        bgcolor: active ? COLORS.slate50 : "transparent",
        opacity: disabled ? 0.55 : 1,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        "&:hover": disabled ? {} : { bgcolor: COLORS.slate50 },
        "&:focus-visible": { outline: `2px solid ${tone}`, outlineOffset: -2 },
        ...sx,
      }}
    >
      {avatar || icon ? (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            color: active ? tone : COLORS.slate500,
          }}
        >
          {avatar || icon}
        </Box>
      ) : null}
      {meta ? (
        <Box sx={{ minWidth: 90, flexShrink: 0 }}>
          {typeof meta === "string" ? (
            <Typography sx={{ fontSize: "0.8rem", color: COLORS.slate700, fontWeight: 700 }}>
              {meta}
            </Typography>
          ) : (
            meta
          )}
        </Box>
      ) : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontSize: "0.875rem", fontWeight: 600, color: COLORS.slate800 }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography noWrap sx={{ fontSize: "0.75rem", color: COLORS.slate500 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {trailing ? (
        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
          {trailing}
        </Box>
      ) : null}
    </Box>
  );
}
